import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ITeam, Team } from '@app/models/Team'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ITeamViewState {
  readonly error: string
  readonly isLoading: boolean
  readonly item: ITeam
  readonly permissions: IEntityPermissions
  readonly showConfirmDelete: boolean
}

const TeamViewRecord = Record({
  error: '',
  isLoading: false,
  item: new Team(),
  permissions: new EntityPermissions(),
  showConfirmDelete: false,
})

export class TeamViewState extends TeamViewRecord implements ITeamViewState {
  public static fromJS(initialTeamView?: Partial<ITeamViewState>) {
    return new TeamViewState(initialTeamView)
  }

  public readonly error: string
  public readonly isLoading: boolean
  public readonly item: Team
  public readonly permissions: EntityPermissions
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<ITeamViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadTeam = 'TEAM_VIEW::LOAD_TEAM',
  LoadTeamRequest = 'TEAM_VIEW::LOAD_TEAM_REQUEST',
  LoadTeamSuccess = 'TEAM_VIEW::LOAD_TEAM_SUCCESS',
  LoadTeamFailure = 'TEAM_VIEW::LOAD_TEAM_FAILURE',
  ResetTeamView = 'TEAM_VIEW::RESET_STATE',
  SetScope = 'TEAM_VIEW::SET_SCOPE',
  ToggleConfirmDelete = 'TEAM_VIEW::TOGGLE_CONFIRM_DELETE',
}

interface ILoadTeamAction {
  type: ActionTypes.LoadTeam
  teamId: string
}

interface ILoadTeamFailureAction {
  error: string
  type: ActionTypes.LoadTeamFailure
}

interface ILoadTeamRequestAction {
  type: ActionTypes.LoadTeamRequest
}

interface ILoadTeamSuccessAction {
  item: Team
  permissions: EntityPermissions
  type: ActionTypes.LoadTeamSuccess
}

interface IResetTeamViewAction {
  type: ActionTypes.ResetTeamView
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadTeamFailureAction
  | ILoadTeamRequestAction
  | ILoadTeamSuccessAction
  | IResetTeamViewAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadTeam(teamId: string): ILoadTeamAction {
  return { type: ActionTypes.LoadTeam, teamId }
}

function loadTeamFailure(error: string): ILoadTeamFailureAction {
  return { error, type: ActionTypes.LoadTeamFailure }
}

function loadTeamRequest(): ILoadTeamRequestAction {
  return { type: ActionTypes.LoadTeamRequest }
}

function loadTeamSuccess(
  item: Team,
  permissions: EntityPermissions,
): ILoadTeamSuccessAction {
  return {
    item,
    permissions,
    type: ActionTypes.LoadTeamSuccess,
  }
}

function resetTeamView(): IResetTeamViewAction {
  return { type: ActionTypes.ResetTeamView }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  loadTeam,
  resetTeamView,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<TeamViewState> = (
  state: TeamViewState = new TeamViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadTeamFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as TeamViewState

    case ActionTypes.LoadTeamRequest:
      return state.set('isLoading', true) as TeamViewState

    case ActionTypes.LoadTeamSuccess:
      return state
        .set('isLoading', false)
        .set('item', action.item)
        .set('permissions', action.permissions) as TeamViewState

    case ActionTypes.ResetTeamView:
      return state.set('isLoading', false) as TeamViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as TeamViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadTeamSaga(action: ILoadTeamAction) {
  try {
    yield put(loadTeamRequest())

    const baseUrl = `/api/Teams/${action.teamId}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as Team

      yield put(
        loadTeamSuccess(
          Team.fromJS(entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadTeamFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadTeam() {
  yield takeLatest(ActionTypes.LoadTeam, loadTeamSaga)
}

export function* teamViewRootSaga() {
  yield fork(watchLoadTeam)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): TeamViewState =>
  state.teamView

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItem = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.item,
  )

export const selectPermissions = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.permissions,
  )

export const selectShowConfirmDelete = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.showConfirmDelete,
  )
